import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Header from '../../components/ui/Header';
import FloatingCTA from '../../components/ui/FloatingCTA';
import HeroSection from './components/HeroSection';
import ProblemSection from './components/ProblemSection';
import SolutionSection from './components/SolutionSection';
import GuidesSection from './components/GuidesSection';
import VoiceSection from './components/VoiceSection';
import PrivacySection from './components/PrivacySection';
import SocialProofSection from './components/SocialProofSection';
import WaitlistSection from './components/WaitlistSection';
import FooterSection from './components/FooterSection';
import { useWaitlist } from '../../contexts/WaitlistContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const LandingPage = () => {
  const { 
    showSuccessModal,
    handleCloseSuccessModal
  } = useWaitlist();

  useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  // Success Modal - Keep this for confirmation after submission
  const SuccessModal = () => (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ 
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'auto'
      }}
    >
      {console.log('✅ SUCCESS MODAL RENDERED IN PORTAL')}
      
      <div className="glassmorphism p-12 rounded-3xl max-w-2xl w-full text-center" style={{ position: 'relative', zIndex: 100000 }}>
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent/20 flex items-center justify-center">
          <Icon name="CheckCircle" size={40} className="text-accent" />
        </div>
        
        <h2 className="text-3xl font-bold text-foreground mb-4">
          You're in!
        </h2>

        <p className="text-lg text-muted-foreground mb-4">
          You're on the early access list for DepthMe.
          Check your email for next steps and your onboarding timeline.
        </p>

        <p className="text-sm text-muted-foreground opacity-60 mt-2 mb-8">
          If you don't see our confirmation email, please check Spam and mark it as 'Not spam' so future updates land in your inbox 🌙
        </p>

        <div className="space-y-4 mb-8">
          <div className="flex items-center justify-center space-x-2 text-accent">
            <Icon name="Mail" size={20} />
            <span>Confirmation sent to your email</span>
          </div>
          
          <div className="flex items-center justify-center space-x-2 text-muted-foreground">
            <Icon name="Calendar" size={20} />
            <span>Beta access begins January 2026</span>
          </div>
        </div>

        <div className="glassmorphism p-6 rounded-2xl mb-8">
          <h3 className="font-semibold text-foreground mb-4">What happens next?</h3>
          <div className="space-y-3 text-sm text-muted-foreground text-left">
            <div className="flex items-start space-x-3">
              <Icon name="Rocket" size={16} className="text-primary mt-0.5" />
              <span>January 2026: Get first access to DepthMe beta</span>
            </div>
          </div>
        </div>

        <Button
          onClick={handleCloseSuccessModal}
          variant="default"
          size="lg"
          className="violet-glow"
        >
          Continue
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-background text-foreground pb-[calc(7rem+env(safe-area-inset-bottom))] md:pb-0">
      {/* Fixed Header */}
      <Header />
      
      {/* Floating CTA */}
      <FloatingCTA />
      
      {/* Main Content */}
      <main className="relative">
        {/* Hero Section - Now contains inline email form */}
        <HeroSection />
        
        {/* Problem Agitation Section */}
        <ProblemSection />
        
        {/* Solution Overview Section */}
        <SolutionSection />
        
        {/* Voice Cloning Section */}
        <VoiceSection />

        {/* Four Guides Section */}
        <GuidesSection />

        {/* Privacy Section */}
        <PrivacySection />
        
        {/* Social Proof Section */}
        <SocialProofSection />
        
        {/* Waitlist Conversion Section */}
        <WaitlistSection />
      </main>
      
      {/* Footer */}
      <FooterSection />

      {/* Success Modal Only - Email modal removed */}
      {showSuccessModal && createPortal(<SuccessModal />, document.body)}
    </div>
  );
};

export default LandingPage;